/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { useDispatch } from "react-redux";
import { getCostDatabase, saveItemVariableRate } from "@Actions/estimation";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_SUCCESS } from "@Services/constants";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";
import SelectDropDown, { type SelectEventArgs } from "@CubeComponents/Mui_Form/SelectDropDown";
import DropDownListComponent from "@CubeComponents/form/DropdownListComponent";
import { portalMeasurementUnitsUrl } from "../../../../services/dataManager";
import axiosWrapper from "@AxiosWrapper";
import type { GetMeasurementUnitsResponse } from "@ServerTypes/units/units.schema";
import type { SaveItemVariableRateRequest } from "@ServerTypes/costDatabase/costDatabase.schema";
import type { Formatter } from "slickgrid-react";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

export type RangeUnit = {
  id: string;
  name: string;
};

type TestData = {
  range: number | string;
  quantity: number | string;
  rate: number | string;
  amount: number | string;
};

type VariableRateState = {
  index: number;
  id?: number;
  itemId?: string;
  rangeFrom: { value: number; error: boolean };
  rangeTo: { value: number | null; error: boolean };
  absoluteRate: { value: number | null; error: boolean };
  differentialRate: { value: number | null; error: boolean };
  isRatePerUnit: boolean;
  perUnitRateLeastCount: { value: number | null; error: boolean };
  error?: boolean;
  menu?: { show: boolean; anchor: HTMLElement | null };
};

type VariableRateType = {
  index: number;
  itemId: string;
  rangeFrom: number;
  rangeTo: number | null;
  differentialRate: number;
  isRatePerUnit: boolean;
  perUnitRateLeastCount: number | null;
};

export type VariableRatePayload = SaveItemVariableRateRequest["variableRate"];

/**
 * A variable rate being edited. `rangeUnit.id` is required by the server but is genuinely absent
 * until the user picks a Range Unit, so the in-progress shape models it as optional. Save is
 * disabled while it is absent; once set, the draft is a complete {@link VariableRatePayload}.
 */
export type VariableRateDraft = Omit<VariableRatePayload, "rangeUnit"> & {
  rangeUnit: { id?: ObjectIdString; name: string };
};

type VariableRateModalProps = {
  closeVariableRateModal: () => void;
  itemId: ObjectIdString;
  costDatabaseVersionId?: string;
  costDatabaseId: ObjectIdString;
  variableRate: VariableRateDraft;
  canEdit?: boolean;
  setVaribleRate: (payload: VariableRatePayload) => void;
};

const VariableRateModal = ({
  closeVariableRateModal,
  itemId,
  costDatabaseVersionId,
  costDatabaseId,
  variableRate,
  setVaribleRate,
  canEdit = true,
}: VariableRateModalProps) => {
  const { t } = useTranslation();
  const errorFound = useRef(false);
  const [variableRateState, setVariableRateState] = useState<VariableRateState[]>([]);
  const [testDataParams, setTestDataParams] = useState<TestData>({
    range: "",
    rate: "",
    quantity: "",
    amount: "",
  });
  const [variableRateParams, setVariableRateParams] = useState({
    isEnabled: variableRate.isEnabled,
    rangeUnit: { id: variableRate.rangeUnit.id, name: variableRate.rangeUnit.name },
  });

  const dispatch = useDispatch();
  console.log(canEdit);
  const testDataParamsHelperRef = useRef(testDataParams);
  /**
   * SlickGrid grid handle. The ref carries the SlickgridReact instance; this file
   * accesses `dataView.items` reads/writes that match the SlickGrid DataView API.
   * Kept as a loosely-typed handle (legacy `any`) because the project-wide
   * `noUncheckedIndexedAccess` would otherwise force non-null assertions on every
   * `items[index]` read — out of scope for type-only changes here.
   */

  const dataGrid = useRef<any>(null);

  function changeIsVariableRateEnabled(e: ChangeEvent<HTMLInputElement>) {
    setVariableRateParams((prev) => ({ ...prev, isEnabled: e.target.checked }));
  }

  // DropDownListComponent declares `onChange?: (e: any) => void`, so this annotation is the
  // declaration site for its synthetic event; `value` is the selected MeasurementUnits _id.
  function onChangeRangeUnit(e: { value: ObjectIdString; target: { itemData: { name: string } } }) {
    const { name } = e.target.itemData;
    setVariableRateParams((prev) => ({ ...prev, rangeUnit: { id: e.value, name } }));
  }

  function calculateItemRate({
    range,
    rulesList,
    errorPresent,
  }: {
    range: number | string;
    rulesList: VariableRateState[];
    errorPresent?: Boolean;
  }) {
    const isErrorFound = errorPresent === undefined ? errorFound.current : errorPresent;

    if (typeof range !== "number" || isErrorFound) {
      return "";
    }

    let totalValue = 0;

    for (const rule of rulesList) {
      const { rangeFrom, rangeTo, differentialRate, isRatePerUnit, perUnitRateLeastCount } = rule;

      const ceilingValue = rangeTo.value ? Math.min(rangeTo.value, range) : range;
      let factor = isRatePerUnit ? Math.ceil((ceilingValue - rangeFrom.value) / perUnitRateLeastCount.value!) : 1;
      factor = factor || 1;

      totalValue += differentialRate.value! * factor;

      if (rangeTo.value && rangeTo.value >= range) {
        break;
      }
    }

    return totalValue;
  }

  function calculateAmountForTestData({
    rate,
    quantity,
    errorPresent,
  }: {
    rate: number | string;
    quantity: number | string;
    errorPresent?: boolean;
  }) {
    const isErrorFound = errorPresent === undefined ? errorFound.current : errorPresent;

    if (typeof quantity !== "number" || typeof rate !== "number" || isErrorFound) {
      return "";
    }

    const amount = parseFloat((rate * quantity).toFixed(2));
    return amount;
  }

  function testDataRangeOnChange(e: ChangeEvent<HTMLInputElement>) {
    const value: number | string = e.target.value === "" ? e.target.value : parseFloat(e.target.value);

    if (value === "" || errorFound.current) {
      setTestDataParams((prev) => ({ ...prev, range: value, rate: "", amount: "" }));
      return;
    }

    const rate = calculateItemRate({ range: value, rulesList: variableRateState });
    const amount = calculateAmountForTestData({ rate, quantity: testDataParams.quantity });

    setTestDataParams((prev) => ({ ...prev, range: value, rate, amount }));
  }

  const testDataQuantityOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value: number | string = e.target.value === "" ? e.target.value : parseFloat(e.target.value);

    if (value === "" || errorFound.current || testDataParams.range === "") {
      setTestDataParams((prev) => ({ ...prev, quantity: value, amount: "" }));
      return;
    }

    const amount = calculateAmountForTestData({ rate: testDataParams.rate, quantity: value });
    setTestDataParams((prev) => ({ ...prev, quantity: value, amount }));
  };

  function changeRowType(index: number) {
    const dataSource = dataGrid.current.dataView.items;
    const rowData = dataSource[index];
    const { rangeTo, rangeFrom, differentialRate, isRatePerUnit } = rowData;
    const targetRateType = !isRatePerUnit;

    const recordsBeforeCurrent = dataSource.slice(0, index);
    const previousRecordAbsoluteRate = index ? dataSource[index - 1].absoluteRate.value : 0;

    if (rangeTo.value === null) {
      const perUnitRateLeastCount = targetRateType ? 1 : null;
      const absoluteRateValue = targetRateType ? null : previousRecordAbsoluteRate + differentialRate.value;

      const updatedCurrentRecord = {
        ...rowData,
        isRatePerUnit: targetRateType,
        perUnitRateLeastCount: { value: perUnitRateLeastCount, error: false },
        absoluteRate: { value: absoluteRateValue, error: false },
        menu: { show: false, anchor: null },
      };

      const finalRecordList = [...recordsBeforeCurrent, updatedCurrentRecord];
      const { error, rate, amount } = rateRulesCompleteErrorValidation(finalRecordList);
      errorFound.current = error;
      setTestDataParams((prev) => ({ ...prev, rate, amount }));

      setVariableRateState(finalRecordList);
      return;
    }

    const perUnitRateLeastCount = targetRateType ? Math.min(1, rangeTo.value - rangeFrom.value) : null;
    const factor = targetRateType ? Math.ceil((rangeTo.value - rangeFrom.value) / perUnitRateLeastCount!) : 1;

    const currentRecordAbsoluteRateUpdated = previousRecordAbsoluteRate + differentialRate.value * factor;

    const updatedCurrentRecord = {
      ...rowData,
      isRatePerUnit: targetRateType,
      perUnitRateLeastCount: { value: perUnitRateLeastCount, error: false },
      absoluteRate: { value: currentRecordAbsoluteRateUpdated, error: false },
      menu: { show: false, anchor: null },
    };

    const recordsAfterCurrent = modifyAbsoluteRateForSubsequentRows(updatedCurrentRecord, dataSource);
    const finalRecordList = [...recordsBeforeCurrent, updatedCurrentRecord, ...recordsAfterCurrent];

    const { error, rate, amount } = rateRulesCompleteErrorValidation(finalRecordList);
    errorFound.current = error;
    setTestDataParams((prev) => ({ ...prev, rate, amount }));

    setVariableRateState(finalRecordList);
  }

  function modifyAbsoluteRateForSubsequentRows(rowData: VariableRateState, rules: VariableRateState[]) {
    const index = rowData ? rowData.index : -1;
    let lastRowAbsoluteRate = rowData ? rowData.absoluteRate.value : 0;

    if (index + 1 === rules.length) return [];

    for (let i = index + 1; i < rules.length; i++) {
      const { rangeFrom, rangeTo, differentialRate, isRatePerUnit, perUnitRateLeastCount } = rules[i]!;

      if (rangeTo.value || !isRatePerUnit) {
        const factor = isRatePerUnit ? Math.ceil((rangeTo.value! - rangeFrom.value) / perUnitRateLeastCount.value!) : 1;
        const absoluteRate = lastRowAbsoluteRate! + differentialRate.value! * factor;
        rules[i]!.absoluteRate = { value: absoluteRate, error: false };
        lastRowAbsoluteRate = absoluteRate;
      } else {
        rules[i]!.absoluteRate = { value: null, error: false };
      }
    }

    return rules.slice(index + 1);
  }

  const modifyElementsAfterwards = ({
    elementsAfterwards,
    lastVariableRateRangeTo,
    incrementIndex,
  }: {
    elementsAfterwards: VariableRateState[];
    lastVariableRateRangeTo: number;
    incrementIndex: boolean;
  }) =>
    elementsAfterwards.map((variableRateRecord) => {
      const { rangeTo, isRatePerUnit, perUnitRateLeastCount, index } = variableRateRecord;
      const rangeFrom = { value: lastVariableRateRangeTo, error: false };
      const newIndex = incrementIndex ? index + 1 : index;

      if (!rangeTo.value || rangeTo.value > lastVariableRateRangeTo) {
        lastVariableRateRangeTo = rangeTo.value!;
      } else {
        rangeTo.value = lastVariableRateRangeTo + 1;
        lastVariableRateRangeTo += 1;
      }

      const output = {
        ...variableRateRecord,
        rangeFrom,
        rangeTo,
        index: newIndex,
        id: newIndex,
        perUnitRateLeastCount: {
          value: perUnitRateLeastCount.value,
          error:
            isRatePerUnit &&
            perUnitRateLeastCount.value &&
            rangeTo.value &&
            perUnitRateLeastCount.value > rangeTo.value - rangeFrom.value,
        },
      };

      return output;
    });

  const rangeToErrorValidation = (value: string | number, rangeFrom: number) => {
    const isErrorFound = value === "" || parseFloat(value.toString()) <= 0 || parseFloat(value.toString()) < rangeFrom;
    return !!isErrorFound;
  };

  function rangeToOnChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const index = parseFloat(e.target.parentElement!.parentElement!.getAttribute("data-index")!);
    const dataSource = dataGrid.current.dataView.items;
    const rowData = dataSource[index];
    const value = e.target.value;

    const isErrorFound = rangeToErrorValidation(value, rowData.rangeFrom.value);

    if (isErrorFound) {
      const updatedRecord = { ...rowData, rangeTo: { value, error: true } };
      const finalRecordList = [...dataSource];
      finalRecordList[index] = updatedRecord;

      const { error, rate, amount } = rateRulesCompleteErrorValidation(finalRecordList);
      errorFound.current = error;
      setTestDataParams((prev) => ({ ...prev, rate, amount }));

      setVariableRateState(finalRecordList);
      return;
    }

    const updatedRecord = { ...rowData, rangeTo: { value: parseFloat(value), error: false } };
    if (updatedRecord.isRatePerUnit) {
      const factor = Math.ceil(
        (updatedRecord.rangeTo.value - updatedRecord.rangeFrom.value) / updatedRecord.perUnitRateLeastCount.value,
      );
      const previousRecordAbsoluteRate = dataSource[updatedRecord.index - 1].absoluteRate.value;

      const newAbsoluteRateForCurrentRecord =
        previousRecordAbsoluteRate + updatedRecord.differentialRate.value * factor;
      updatedRecord.absoluteRate = { value: newAbsoluteRateForCurrentRecord, error: false };
    }

    const recordsBefore = dataSource.slice(0, index);
    const recordsAfter = dataSource.slice(index + 1);

    const recordsAfterModified = modifyElementsAfterwards({
      elementsAfterwards: recordsAfter,
      lastVariableRateRangeTo: parseFloat(value),
      incrementIndex: false,
    });
    const recordsAfterNewAddedRowWithModifiedAbsoluteRate = modifyAbsoluteRateForSubsequentRows(updatedRecord, [
      ...recordsBefore,
      updatedRecord,
      ...recordsAfterModified,
    ]);
    const finalRecordList = [...recordsBefore, updatedRecord, ...recordsAfterNewAddedRowWithModifiedAbsoluteRate];

    const { error, rate, amount } = rateRulesCompleteErrorValidation(finalRecordList);
    errorFound.current = error;
    setTestDataParams((prev) => ({ ...prev, rate, amount }));
    setVariableRateState(finalRecordList);
  }

  const absoluteRateErrorValidation = (value: string | number) => {
    const errorCurrent = value === "" || parseFloat(value.toString()) <= 0;
    return errorCurrent;
  };

  function absoluteRateOnChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const index = parseFloat(e.target.parentElement!.parentElement!.getAttribute("data-index")!);
    const rowData = dataGrid.current.dataView.items[index];
    const value = e.target.value;

    const { rangeFrom, rangeTo, isRatePerUnit, perUnitRateLeastCount } = rowData;
    const recordsBefore = dataGrid.current.dataView.items.slice(0, index);

    const errorCheck = absoluteRateErrorValidation(value);

    if (errorCheck) {
      const updatedCurrentRecord = { ...rowData, absoluteRate: { value, error: true } };
      const recordsAfterCurrent = dataGrid.current.dataView.items.slice(index + 1);

      const finalRecordList = [...recordsBefore, updatedCurrentRecord, ...recordsAfterCurrent];
      const { error, rate, amount } = rateRulesCompleteErrorValidation(finalRecordList);
      errorFound.current = error;
      setTestDataParams((prev) => ({ ...prev, rate, amount }));
      setVariableRateState(finalRecordList);
      return;
    }

    const previousRecordAbsoluteRate = index ? dataGrid.current.dataView.items[index - 1].absoluteRate.value : 0;
    const factor = isRatePerUnit ? Math.ceil((rangeTo.value - rangeFrom.value) / perUnitRateLeastCount.value) : 1;

    const differentialRateForCurrentRecord = (parseFloat(value) - previousRecordAbsoluteRate) / factor;

    const updatedCurrentRecord = {
      ...rowData,
      absoluteRate: { value: parseFloat(value), error: differentialRateForCurrentRecord <= 0 },
      differentialRate: { value: differentialRateForCurrentRecord, error: differentialRateForCurrentRecord <= 0 },
    };

    const recordsAfterCurrent = modifyAbsoluteRateForSubsequentRows(
      updatedCurrentRecord,
      dataGrid.current.dataView.items,
    );
    const finalRecordList = [...recordsBefore, updatedCurrentRecord, ...recordsAfterCurrent];
    const { error, rate, amount } = rateRulesCompleteErrorValidation(finalRecordList);
    errorFound.current = error;
    setTestDataParams((prev) => ({ ...prev, rate, amount }));
    setVariableRateState(finalRecordList);
  }

  function differentialRateErrorValidation(value: string | number) {
    const isErrorFound = value === "" || parseFloat(value.toString()) <= 0;
    return isErrorFound;
  }

  function differentialRateOnChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const index = parseFloat(e.target.parentElement!.parentElement!.getAttribute("data-index")!);
    const dataSource = dataGrid.current.dataView.items;
    const rowData = dataSource[index];
    const value = e.target.value;

    const recordsBeforeCurrent = dataSource.slice(0, index);

    const { isRatePerUnit, rangeTo, rangeFrom, perUnitRateLeastCount } = rowData;
    const isErrorFound = differentialRateErrorValidation(value);

    if (isErrorFound) {
      const recordsAfterCurrent = dataSource.slice(index + 1);
      const currentRecordUpdated = { ...rowData, differentialRate: { value, error: true } };

      const finalRecordList = [...recordsBeforeCurrent, currentRecordUpdated, ...recordsAfterCurrent];
      const { error, rate, amount } = rateRulesCompleteErrorValidation(finalRecordList);
      errorFound.current = error;
      setTestDataParams((prev) => ({ ...prev, rate, amount }));
      setVariableRateState(finalRecordList);
      return;
    }

    if (index + 1 === dataSource.length) {
      const newAbsoluteRate = isRatePerUnit
        ? null
        : (dataSource[index - 1]?.absoluteRate?.value || 0) + parseFloat(value);

      const currentRecordUpdated = {
        ...rowData,
        differentialRate: { value: parseFloat(value), error: false },
        absoluteRate: { value: newAbsoluteRate, error: false },
      };

      const finalRecordList = [...recordsBeforeCurrent, currentRecordUpdated];

      const { error, rate, amount } = rateRulesCompleteErrorValidation(finalRecordList);
      errorFound.current = error;
      setTestDataParams((prev) => ({ ...prev, rate, amount }));
      setVariableRateState(finalRecordList);
      return;
    }

    const currentRecordUpdated = { ...rowData, differentialRate: { value: parseFloat(value), error: false } };

    const factor = isRatePerUnit ? Math.ceil((rangeTo.value - rangeFrom.value) / perUnitRateLeastCount.value) : 1;
    const previousRecordAbsoluteRate = dataSource[index - 1].absoluteRate.value;

    const newAbsoluteRateForCurrentRecord = previousRecordAbsoluteRate + parseFloat(value) * factor;
    currentRecordUpdated.absoluteRate = { value: newAbsoluteRateForCurrentRecord, error: false };

    const recordsAfterCurrent = modifyAbsoluteRateForSubsequentRows(currentRecordUpdated, dataSource);
    const finalRecordList = [...recordsBeforeCurrent, currentRecordUpdated, ...recordsAfterCurrent];

    const { error, rate, amount } = rateRulesCompleteErrorValidation(finalRecordList);
    errorFound.current = error;
    setTestDataParams((prev) => ({ ...prev, rate, amount }));
    setVariableRateState(finalRecordList);
    // calculateItemRate({ range: testDataHelperRef.current.range, rulesList: finalRecordList });
  }

  function perUnitRateLeastCountOnChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const dataSource = dataGrid.current.dataView.items;
    const index = parseFloat(e.target.parentElement!.parentElement!.getAttribute("data-index")!);
    const rowData = dataSource[index];
    const value = e.target.value;

    const { rangeTo, rangeFrom, differentialRate } = rowData;

    const isErrorFound = differentialRateErrorValidation(value);
    const recordsBeforeCurrent = dataSource.slice(0, index);

    if (isErrorFound || rangeTo.value === null) {
      const recordsAfter = dataSource.slice(index + 1);
      const perUnitRateLeastCountValue = isErrorFound ? value : parseFloat(value);
      const updatedRecord = {
        ...rowData,
        perUnitRateLeastCount: { value: perUnitRateLeastCountValue, error: isErrorFound },
      };
      const finalRecordList = [...recordsBeforeCurrent, updatedRecord, ...recordsAfter];

      const { error, rate, amount } = rateRulesCompleteErrorValidation(finalRecordList);
      errorFound.current = error;
      setTestDataParams((prev) => ({ ...prev, rate, amount }));
      setVariableRateState(finalRecordList);
      return;
    }

    const factor = Math.ceil((rangeTo.value - rangeFrom.value) / parseFloat(value));
    const previousRecordAbsoluteRate = index ? dataSource[index - 1].absoluteRate.value : 0;
    const currentRecordAbsoluteRateUpdated = previousRecordAbsoluteRate + differentialRate.value * factor;
    const updatedCurrentRecord = {
      ...rowData,
      perUnitRateLeastCount: { value: parseFloat(value), error: rangeTo.value - rangeFrom.value < parseFloat(value) },
      absoluteRate: { value: currentRecordAbsoluteRateUpdated, error: false },
    };

    const recordsAfterCurrent = modifyAbsoluteRateForSubsequentRows(updatedCurrentRecord, dataSource);
    const finalRecordList = [...recordsBeforeCurrent, updatedCurrentRecord, ...recordsAfterCurrent];

    const { error, rate, amount } = rateRulesCompleteErrorValidation(finalRecordList);
    errorFound.current = error;
    setTestDataParams((prev) => ({ ...prev, rate, amount }));
    setVariableRateState(finalRecordList);
  }

  function addNewRow(rowData: VariableRateState) {
    if (!errorFound.current) {
      const { index, itemId, rangeTo, absoluteRate } = rowData;
      const data = dataGrid.current.dataView.items;

      if (index + 1 < data.length) {
        const elementsUptoCurrentRecord = data.slice(0, index + 1);
        const elementsAfterwards = data.slice(index + 1);

        const lastVariableRateRangeTo = rangeTo.value! + 1;

        const newRecord: VariableRateState = {
          itemId,
          index: index + 1,
          id: index + 1,
          isRatePerUnit: false,
          perUnitRateLeastCount: { value: null, error: false },
          rangeFrom: { value: rangeTo.value!, error: false },
          rangeTo: { value: lastVariableRateRangeTo, error: false },
          absoluteRate: { value: absoluteRate.value! + 1, error: false },
          differentialRate: { value: 1, error: false },
          menu: { show: false, anchor: null },
        };

        const elementsAfterwardsModified = modifyElementsAfterwards({
          elementsAfterwards,
          lastVariableRateRangeTo,
          incrementIndex: true,
        });

        const recordsAfterNewAddedRowWithModifiedAbsoluteRate = modifyAbsoluteRateForSubsequentRows(newRecord, [
          ...elementsUptoCurrentRecord,
          newRecord,
          ...elementsAfterwardsModified,
        ]);
        const finalData = [...elementsUptoCurrentRecord, newRecord, ...recordsAfterNewAddedRowWithModifiedAbsoluteRate];

        const { error, rate, amount } = rateRulesCompleteErrorValidation(finalData);
        errorFound.current = error;
        setTestDataParams((prev) => ({ ...prev, rate, amount }));
        setVariableRateState(finalData);
      }
    }
  }

  function deleteRow(rowData: VariableRateState) {
    const { index, rangeFrom } = rowData;

    const data: VariableRateState[] = dataGrid.current.dataView.items;
    const nextRecord = index + 1 < data.length && data[index + 1];

    if (nextRecord) {
      const recordsBeforeCurrentRecord = data.slice(0, index);
      const recordsAfterNextRecord = data.slice(index + 2);
      const recordsAfterNextRecordModified = recordsAfterNextRecord.map((record) => ({
        ...record,
        index: record.index - 1,
        id: record.index - 1,
      }));
      const updatedNextRecord = {
        ...nextRecord,
        rangeFrom: { value: rangeFrom.value, error: false },
        index: nextRecord.index - 1,
        id: nextRecord.index - 1,
      };

      const finalData = [...recordsBeforeCurrentRecord, updatedNextRecord, ...recordsAfterNextRecordModified];
      const recordsAfterDeletedRecordsWithModifiedAbsoluteRate = modifyAbsoluteRateForSubsequentRows(
        recordsBeforeCurrentRecord[recordsBeforeCurrentRecord.length - 1]!,
        finalData,
      );
      const finalRecordList = [...recordsBeforeCurrentRecord, ...recordsAfterDeletedRecordsWithModifiedAbsoluteRate];

      const { error, rate, amount } = rateRulesCompleteErrorValidation(finalRecordList);
      errorFound.current = error;
      setTestDataParams((prev) => ({ ...prev, rate, amount }));
      setVariableRateState(finalRecordList);
    }
  }

  async function saveVaribleRateParams() {
    const variableRateListPayload = variableRateState.map((record) => {
      const { index, itemId, rangeFrom, rangeTo, differentialRate, isRatePerUnit, perUnitRateLeastCount } = record;

      return {
        index,
        itemId,
        rangeFrom: rangeFrom.value,
        rangeTo: rangeTo.value,
        differentialRate: differentialRate.value,
        isRatePerUnit,
        perUnitRateLeastCount: perUnitRateLeastCount.value,
      };
    });

    const rangeUnitId = variableRateParams.rangeUnit.id;
    // The server rejects a variable rate without a range unit; the Save button is disabled until
    // one is picked, so this only guards against a programmatic call.
    if (!rangeUnitId) return;

    const newVariableRate = {
      isEnabled: variableRateParams.isEnabled,
      rangeUnit: { id: rangeUnitId, name: variableRateParams.rangeUnit.name },
      rules: JSON.stringify(variableRateListPayload),
    };

    const data = {
      itemId,
      variableRate: newVariableRate,
      costDatabaseId: costDatabaseId,
    };

    const response = await saveItemVariableRate(data);

    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("The changes have been saved successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      setVaribleRate(newVariableRate);
      dispatch(getCostDatabase({ _id: costDatabaseId }));
    }

    closeVariableRateModal();
  }

  function rateRulesCompleteErrorValidation(rules: VariableRateState[]) {
    let lastRangeTo: number | null = 0;
    let errorPresent = false;

    rules.map((record) => {
      const { rangeFrom, rangeTo, absoluteRate, differentialRate, perUnitRateLeastCount, isRatePerUnit } = record;

      if (
        typeof rangeTo.value === "string" ||
        (rangeTo.value !== null && rangeTo.value <= 0) ||
        rangeFrom.value !== lastRangeTo ||
        (rangeTo.value !== null &&
          (rangeTo.value < rangeFrom.value || rangeTo.value - rangeFrom.value < perUnitRateLeastCount.value!)) ||
        typeof absoluteRate.value === "string" ||
        (absoluteRate.value !== null && absoluteRate.value <= 0) ||
        (!isRatePerUnit && absoluteRate.value === null) ||
        differentialRate.value === null ||
        typeof differentialRate.value === "string" ||
        differentialRate.value <= 0 ||
        typeof perUnitRateLeastCount.value === "string" ||
        (perUnitRateLeastCount.value != null && perUnitRateLeastCount.value <= 0)
      ) {
        errorPresent = true;
      }

      lastRangeTo = rangeTo.value;
    });

    const { range, quantity } = testDataParamsHelperRef.current;
    const rate = errorPresent ? "" : calculateItemRate({ range, rulesList: rules, errorPresent });
    const amount = errorPresent ? "" : calculateAmountForTestData({ rate, quantity, errorPresent });

    return { error: errorPresent, rate, amount };
  }

  function initialiseVariableRateRules(rules: VariableRateType[]) {
    let lastRowAbsoluteRate = 0;

    const rulesToSet: VariableRateState[] = rules.map((rule) => {
      const { index, itemId, rangeFrom, rangeTo, differentialRate, isRatePerUnit, perUnitRateLeastCount } = rule;
      let absoluteRate: number | null = null;

      if (rangeTo || !isRatePerUnit) {
        const factor = isRatePerUnit ? Math.ceil((rangeTo! - rangeFrom) / perUnitRateLeastCount!) : 1;
        absoluteRate = lastRowAbsoluteRate + differentialRate * factor;
        lastRowAbsoluteRate = absoluteRate;
      }

      return {
        index,
        itemId,
        isRatePerUnit,
        rangeFrom: { value: rangeFrom, error: false },
        rangeTo: { value: rangeTo, error: false },
        differentialRate: { value: differentialRate, error: false },
        absoluteRate: { value: absoluteRate, error: false },
        perUnitRateLeastCount: { value: perUnitRateLeastCount, error: false },
        menu: { show: false, anchor: null },
      };
    });

    return rulesToSet;
  }

  function initialiseVariableRate() {
    const modifiedVariableRateSchema = initialiseVariableRateRules(JSON.parse(variableRate.rules));

    setVariableRateState(modifiedVariableRateSchema.map((record, index) => ({ ...record, id: index })));
  }

  useEffect(() => {
    initialiseVariableRate();
  }, []);

  useEffect(() => {
    testDataParamsHelperRef.current = testDataParams;
  }, [testDataParams]);

  const ratePerUnitFormatter: Formatter<VariableRateState> = (_row, _cell, _value, _colDef, data) => {
    const container = document.createElement("div");
    const root = createRoot(container);

    const { isRatePerUnit } = data;

    if (isRatePerUnit) {
      root.render(
        <div className="flex flex-row gap-2 items-center">
          <Text>{t("Per Every")} </Text>
          <div className="w-20">
            <TextFieldComponent
              type="number"
              inputProps={{ min: 0, max: 100000, step: 0.1 }}
              error={data.perUnitRateLeastCount.error}
              disabled={errorFound.current && !data.perUnitRateLeastCount.error}
              variant="outlined"
              data-index={data.index}
              value={data.perUnitRateLeastCount.value}
              onChange={perUnitRateLeastCountOnChange}
            />
          </div>
          <p className="w-32 overflow-hidden whitespace-nowrap text-ellipsis text-sm font-semibold">
            {variableRateParams.rangeUnit.name}
          </p>
        </div>,
      );
    }

    return container;
  };

  const addIconFormatter: Formatter<VariableRateState> = (_row, _cell, _value, _colDef, data) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    if (data.rangeTo.value !== null) {
      root.render(
        <IconComponent
          isEnabled={!errorFound.current}
          name="add-icon"
          width="1.0625rem"
          height="1.0625rem"
          onClick={() => addNewRow(data)}
        />,
      );
    }
    return container;
  };

  const rangeFromFormatter: Formatter<VariableRateState> = (_row, _cell, _value, _colDef, data) => {
    const container = document.createElement("div");
    const root = createRoot(container);

    root.render(
      <div className="flex flex-row items-center justify-between gap-2">
        <p>{data.rangeFrom.value}</p>
        <p className="w-40 overflow-hidden whitespace-nowrap text-ellipsis text-sm font-semibold">
          {variableRateParams.rangeUnit.name}
        </p>
      </div>,
    );
    return container;
  };

  const rangeToFormatter: Formatter<VariableRateState> = (_row, _cell, _value, _colDef, data) => {
    const container = document.createElement("div");
    container.classList.add("w-full");
    const root = createRoot(container);

    if (data.rangeTo.value === null) {
      root.render(<Text className="text-sm font-semibold">{t("Onwards")}</Text>);
    } else {
      root.render(
        <div className="flex flex-row gap-2 items-center">
          <div className="w-40">
            <TextFieldComponent
              type="number"
              inputProps={{ min: 0, max: 100000, step: 0.1 }}
              disabled={errorFound.current && !data.rangeTo.error}
              error={data.rangeTo.error}
              variant="outlined"
              value={data.rangeTo.value}
              data-index={data.index}
              onChange={rangeToOnChange}
            />
          </div>
          <p className="w-40 overflow-hidden whitespace-nowrap text-ellipsis text-sm font-semibold">
            {variableRateParams.rangeUnit.name}
          </p>
        </div>,
      );
    }

    return container;
  };

  const absoluteRateFormatter: Formatter<VariableRateState> = (_row, _cell, _value, _colDef, data) => {
    const container = document.createElement("div");
    container.classList.add("w-full");
    const root = createRoot(container);

    const { isRatePerUnit } = data;

    if (!isRatePerUnit) {
      root.render(
        <TextFieldComponent
          type="number"
          inputProps={{ min: 0, max: 100000, step: 0.1 }}
          disabled={errorFound.current && !data.absoluteRate.error}
          error={data.absoluteRate.error}
          variant="outlined"
          data-index={data.index}
          value={data.absoluteRate.value}
          onChange={absoluteRateOnChange}
        />,
      );
    }
    return container;
  };

  const differentialRateFormatter: Formatter<VariableRateState> = (_row, _cell, _value, _colDef, data) => {
    const container = document.createElement("div");
    container.classList.add("w-full");
    const root = createRoot(container);

    const { index } = data;

    if (index) {
      root.render(
        <TextFieldComponent
          type="number"
          inputProps={{ min: 0, max: 100000, step: 0.1 }}
          error={data.differentialRate.error}
          disabled={errorFound.current && !data.differentialRate.error}
          variant="outlined"
          data-index={index}
          value={data.differentialRate.value}
          onChange={differentialRateOnChange}
        />,
      );
    }
    return container;
  };

  const selectActionFromMenu = (e: SelectEventArgs, data: VariableRateState) => {
    const menuId = e.item.id;

    if (menuId == "1") {
      addNewRow(data);
      return;
    }

    if (menuId == "2") {
      deleteRow(data);
      return;
    }

    if (menuId == "3") {
      changeRowType(data.index);
    }
  };

  const verticalEllipsisFormatter: Formatter<VariableRateState> = (_row, _cell, _value, _colDef, data) => {
    const container = document.createElement("div");
    const root = createRoot(container);

    const changeToPerUnitRateDisabled = data.index === 0;
    const disableAddRow = data.index + 1 === dataGrid.current.dataView.items.length;
    const disableDeleteRow = !data.index || data.index + 1 === dataGrid.current.dataView.items.length;

    const itemsList = [
      { id: "1", text: "Create Below", disabled: disableAddRow },
      { id: "2", text: "Delete", disabled: disableDeleteRow },
      {
        id: "3",
        text: data.isRatePerUnit ? "Change to Standard Rate" : "Change to Per Unit Rate",
        disabled: changeToPerUnitRateDisabled,
      },
    ];
    root.render(
      <SelectDropDown
        select={(e: SelectEventArgs) => selectActionFromMenu(e, data)}
        items={itemsList}
        cssClass="e-caret-hide bg-transparent"
        disabled={errorFound.current}
      >
        <span className="e-more-vertical-1 e-icons" />
      </SelectDropDown>,
    );

    return container;
  };

  const rangeFromHeaderRenderer = () => {
    const container = document.createElement("div");
    const root = createRoot(container);

    root.render(
      <div className="flex flex-row gap-2 items-center">
        <Text className="font-semibold">{t("From")}</Text>
        <IconComponent
          name="details"
          toolTipContent={t(
            'Values under "From" are NOT included in the rate calculation, except for the value in the first row',
          )}
          position="bottom"
          width="1.25rem"
          height="1.25rem"
        />
      </div>,
    );
    return container;
  };

  const rangeToHeaderRenderer = () => {
    const container = document.createElement("div");
    const root = createRoot(container);

    root.render(
      <div className="flex flex-row gap-2 items-center">
        <Text className="font-semibold">{t("To")}</Text>
        <IconComponent
          name="details"
          toolTipContent={t('Values under "To" are included in the rate calculation')}
          position="bottom"
          width="1.25rem"
          height="1.25rem"
        />
      </div>,
    );
    return container;
  };

  const differentialRateHeaderRenderer = () => {
    const container = document.createElement("div");
    const root = createRoot(container);

    root.render(
      <div className="flex flex-row gap-2 items-center">
        <Text className="font-semibold">{t("Differential Rate")}</Text>
        <IconComponent
          name="details"
          toolTipContent={t("Differential Rate = Absolute Rate (current row) - Absolute Rate (previous row)")}
          position="bottom"
          width="1.25rem"
          height="1.25rem"
        />
      </div>,
    );

    return container;
  };

  const columnDefinitions = useMemo<any>(
    () => [
      {
        id: "addIcon",
        field: "addIcon",
        minWidth: 50,
        formatter: addIconFormatter,
      },
      {
        id: "rangeFrom",
        field: "rangeFrom",
        name: t("From"),
        minWidth: 160,
        _customHeaderRenderer: rangeFromHeaderRenderer,
        formatter: rangeFromFormatter,
      },
      {
        id: "rangeTo",
        field: "rangeTo",
        name: t("To"),
        minWidth: 220,
        _customHeaderRenderer: rangeToHeaderRenderer,
        formatter: rangeToFormatter,
      },
      {
        id: "absoluteRate",
        field: "absoluteRate",
        name: t("Absolute Rate"),
        minWidth: 160,
        formatter: absoluteRateFormatter,
      },
      {
        id: "differentialRate",
        field: "differentialRate",
        name: t("Differential Rate"),
        minWidth: 180,
        _customHeaderRenderer: differentialRateHeaderRenderer,
        formatter: differentialRateFormatter,
      },
      {
        id: "isRatePerUnit",
        field: "isRatePerUnit",
        minWidth: 220,
        formatter: ratePerUnitFormatter,
      },
      {
        id: "verticalEllipsis",
        field: "verticalEllipsis",
        minWidth: 70,
        formatter: verticalEllipsisFormatter,
      },
    ],

    [variableRateParams.rangeUnit.id],
  );

  const getVariableRateGrid = () => (
    <div className="flex w-full h-90">
      <SlickGrid1
        ref={dataGrid}
        columnDefinitions={columnDefinitions}
        dataset={variableRateState}
        hideNoDataIllustration
      />
    </div>
  );

  const header = () => (
    <div className="flex gap-2 items-center">
      <Text>{t("Variable Rate")}</Text>
      <ToggleSwitchComponent checked={variableRateParams.isEnabled} onChange={changeIsVariableRateEnabled} />
    </div>
  );

  const [dropdownData, setDropDownData] = useState<NonNullable<GetMeasurementUnitsResponse["data"]>>([]);

  const fetchDataForDropDown = async () => {
    const response = await axiosWrapper.post<GetMeasurementUnitsResponse>(portalMeasurementUnitsUrl, { value: {} });

    if (response.data.status && response.data.data) {
      setDropDownData(response.data.data);
    }
  };

  useEffect(() => {
    fetchDataForDropDown();
  }, []);

  const content = () => (
    <div
      className={
        variableRateParams.isEnabled
          ? "opacity-100 pointer-events-auto cursor-auto pt-4 text-sm"
          : "opacity-50 pointer-events-none cursor-not-allowed pt-4 text-sm"
      }
    >
      <div className="flex flex-row gap-4 items-center pb-4">
        <Text>{t("Range Unit")}</Text>
        <DropDownListComponent
          enabled={!costDatabaseVersionId}
          width="12.5rem"
          value={variableRateParams.rangeUnit.id}
          dataSource={dropdownData}
          fields={{ text: "name", value: "_id" }}
          onChange={onChangeRangeUnit}
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        />
      </div>
      {getVariableRateGrid()}
      {testData()}
    </div>
  );

  const testData = () => (
    <div className="flex flex-col gap-6 w-3/5 pt-8">
      <div className="flex flex-row items-center gap-10">
        <div className="flex flex-row w-2/3 justify-between items-center">
          <Text>{t("Range")}</Text>
          <div className="flex flex-row gap-2 w-60 items-center">
            <TextFieldComponent
              type="number"
              inputProps={{ min: 0, max: 100000, step: 0.1 }}
              disabled={errorFound.current}
              variant="outlined"
              value={testDataParams.range}
              onChange={testDataRangeOnChange}
            />
            <p className="font-semibold truncate w-16">{variableRateParams.rangeUnit.name}</p>
          </div>
        </div>

        <div className="flex flex-row w-1/3 justify-between">
          <Text>{t("Rate")}</Text>
          <div className="flex flex-row gap-2 w-50">
            <p>{testDataParams.rate === "" ? "-" : parseFloat(testDataParams.rate.toString()).toFixed(2)}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-row items-center gap-10">
        <div className="flex flex-row w-2/3 justify-between items-center">
          <Text>{t("Quantity")}</Text>
          <div className="flex flex-row gap-2 w-60 items-center">
            <TextFieldComponent
              type="number"
              inputProps={{ min: 0, max: 100000, step: 0.1 }}
              disabled={errorFound.current}
              variant="outlined"
              value={testDataParams.quantity}
              onChange={testDataQuantityOnChange}
            />
            <p className="font-semibold truncate w-16">{variableRateParams.rangeUnit.name}</p>
          </div>
        </div>

        <div className="flex flex-row w-1/3 justify-between">
          <Text>{t("Amount")}</Text>
          <div className="flex flex-row gap-2 w-50">
            <p>{testDataParams.amount === "" ? "-" : parseFloat(testDataParams.amount.toString()).toFixed(2)}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const footer = () => (
    <div className="flex flex-row items-center gap-4 pt-6">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        disabled={errorFound.current || !variableRateParams.rangeUnit.id}
        onClick={saveVaribleRateParams}
      >
        <Text>{t("Save")}</Text>
      </ButtonComponent>
    </div>
  );

  return (
    <ModalStateDialogComponent
      width="69.375rem"
      headerTitle={header()}
      modelContent={content()}
      footerContent={footer()}
      isVisible
      onCancel={closeVariableRateModal}
    />
  );
};

export default VariableRateModal;
